import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { 
  Music, 
  Calendar, 
  Clock, 
  User, 
  DollarSign, 
  CheckCircle, 
  AlertCircle,
  ChevronLeft,
  RefreshCw,
  Home,
  MapPin,
  RefreshCcw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  LogOut,
  FileDown
} from "lucide-react";
import { formatDate, formatDateWithDay } from "../utils/dateFormat";
import SwapRequestDialog from "../components/admin/SwapRequestDialog";

// Helper functions
const getStatusBadge = (status, isPast) => {
  if (isPast && status !== 'cancelled') {
    return (
      <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-bold">
        COMPLETED
      </span>
    );
  }
  
  const styles = {
    pending: 'bg-amber-100 text-amber-700 border-amber-300',
    paid: 'bg-green-100 text-green-700 border-green-300',
    completed: 'bg-gray-200 text-gray-700 border-gray-300',
    cancelled: 'bg-red-100 text-red-700 border-red-300'
  };
  
  const labels = {
    pending: 'PENDING PAYMENT',
    paid: 'CONFIRMED',
    completed: 'COMPLETED',
    cancelled: 'CANCELLED'
  };
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${styles[status] || styles.pending}`}>
      {labels[status] || status?.toUpperCase()}
    </span>
  );
};

const getSlotTime = (slotType) => {
  return slotType === 'afternoon' ? '1:00 PM - 5:00 PM' : '6:00 PM - 10:00 PM';
};

const getSlotLabel = (slotType) => {
  return slotType === 'afternoon' ? 'Afternoon' : 'Evening';
};

const getSwapStatusBadge = (session) => {
  if (session.swap_request_status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-bold">
        <AlertTriangle size={12} />
        SWAP PENDING
      </span>
    );
  }
  if (session.swap_request_status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-bold">
        SWAP REJECTED
      </span>
    );
  }
  return null;
};

// Session Card Component - Defined outside to prevent re-creation
const SessionCard = ({ session, isUpcoming, onRequestSwap }) => {
  const swapBadge = getSwapStatusBadge(session);
  const canRequestSwap = (!session.swap_request_status || session.swap_request_status === 'rejected') 
    && session.status !== 'completed' 
    && session.status !== 'cancelled'
    && isUpcoming;
  
  return (
    <div 
      className={`bg-white border-2 rounded-lg overflow-hidden shadow-sm ${
        session.swap_request_status === 'pending' ? 'border-amber-400' : 'border-gray-200'
      }`}
      data-testid={`session-card-${session.booking_code}`}
    >
      {/* Card Header - Date & Time */}
      <div className={`px-4 py-3 ${
        isUpcoming ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'
      }`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Calendar size={16} />
            <span className="font-bold">{formatDateWithDay(session.date)}</span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <Clock size={14} />
            <span>{getSlotLabel(session.slot_type)}</span>
          </div>
        </div>
      </div>
      
      {/* Card Body */}
      <div className="p-4 space-y-3">
        {/* Booking Code & Status */}
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs text-gray-500 mb-1">Booking Code</p>
            <p className="font-mono font-bold text-lg text-purple-600 notranslate" translate="no">
              {session.booking_code}
            </p>
          </div>
          <div className="text-right">
            {getStatusBadge(session.status, session.is_past)}
          </div>
        </div>
        
        {/* Resident Info */}
        <div className="flex items-start gap-2 text-sm">
          <User size={16} className="text-gray-400 mt-0.5 shrink-0" />
          <div>
            <p className="font-bold notranslate" translate="no">{session.resident_name}</p>
            {session.block_unit && (
              <p className="text-gray-500 text-xs flex items-center gap-1">
                <MapPin size={12} /> {session.block_unit}
              </p>
            )}
          </div>
        </div>
        
        {/* Time */}
        <div className="text-sm text-gray-600 bg-gray-50 rounded px-3 py-2">
          <Clock size={14} className="inline mr-2" />
          {getSlotTime(session.slot_type)}
        </div>
        
        {/* Swap Status Badge */}
        {swapBadge && (
          <div className="pt-1">
            {swapBadge}
            {session.swap_request_status === 'pending' && session.swap_requested_kj_name && (
              <p className="text-xs text-amber-700 mt-1">
                Replacement: <span className="font-bold notranslate" translate="no">{session.swap_requested_kj_name}</span>
              </p>
            )}
          </div>
        )}
        
        {/* Price & Allowance */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
          <div className="text-sm text-gray-600">
            Session Rate
          </div>
          <div className="font-mono font-bold text-lg">${session.price}</div>
        </div>
      </div>
      
      {/* Card Footer - Action Button */}
      {canRequestSwap && (
        <div className="px-4 pb-4">
          <button
            onClick={() => onRequestSwap(session)}
            className="w-full py-3 bg-purple-100 text-purple-700 border-2 border-purple-400 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-purple-200 active:bg-purple-300 transition-colors"
            data-testid={`request-swap-btn-${session.booking_code}`}
          >
            <RefreshCcw size={18} />
            Request Swap
          </button>
        </div>
      )}
    </div>
  );
};

const KJConsolePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sessions, setSessions] = useState({
    upcoming_sessions: [],
    past_sessions: [],
    stats: {
      upcoming_count: 0,
      completed_this_month: 0,
      allowance_earned_this_month: 0,
      is_external_kj: false
    },
    kj_name: "",
    kj_type: ""
  });
  
  // Mobile UI state
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' | 'past'
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [expandedPast, setExpandedPast] = useState(false);
  
  // Swap Request Dialog state
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [selectedBookingForSwap, setSelectedBookingForSwap] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/kj/my-sessions`);
      setSessions(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        logout();
        navigate('/auth');
      } else {
        toast.error("Failed to load sessions");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    // Redirect admins away from KJ pages
    if (user.is_admin) {
      navigate('/admin');
      return;
    }
    fetchSessions();
    
    // Auto refresh every 60 seconds
    const interval = setInterval(fetchSessions, 60000);
    return () => clearInterval(interval);
  }, [user, navigate, fetchSessions]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success("Logged out successfully");
  };

  const handleRequestSwap = (session) => {
    setSelectedBookingForSwap(session);
    setShowSwapDialog(true);
  };

  const handleSwapRequested = () => {
    fetchSessions();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 bg-purple-600 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Music size={24} />
              <div>
                <h1 className="font-bold text-lg leading-tight">KJ Console</h1>
                <p className="text-xs text-purple-200 notranslate" translate="no">{sessions.kj_name}</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={fetchSessions}
                disabled={refreshing}
                className="p-2 hover:bg-purple-700 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 bg-purple-700 hover:bg-purple-800 rounded-lg text-sm font-bold flex items-center gap-2"
              >
                <Home size={16} />
                Dashboard
              </button>
              <button
                onClick={handleLogout}
                className="px-3 py-2 bg-white text-purple-600 hover:bg-gray-100 rounded-lg text-sm font-bold flex items-center gap-2"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 hover:bg-purple-700 rounded-lg"
            >
              {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-purple-500 bg-purple-700 px-4 py-3 space-y-2">
            <button
              onClick={() => { fetchSessions(); setShowMobileMenu(false); }}
              className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-500 rounded-lg text-left flex items-center gap-3"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              Refresh Sessions
            </button>
            <button
              onClick={() => { navigate('/dashboard'); setShowMobileMenu(false); }}
              className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-500 rounded-lg text-left flex items-center gap-3"
            >
              <Home size={18} />
              Back to Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="w-full py-3 px-4 bg-white text-purple-700 hover:bg-gray-100 rounded-lg text-left flex items-center gap-3 font-bold"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        )}
      </header>

      {/* Stats Cards - Horizontal scroll on mobile */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible">
            {/* Upcoming */}
            <div className="flex-shrink-0 w-[140px] md:flex-1 bg-purple-50 border-2 border-purple-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-purple-600">{sessions.stats.upcoming_count}</div>
              <div className="text-xs text-purple-700 font-bold">UPCOMING</div>
            </div>
            
            {/* Completed */}
            <div className="flex-shrink-0 w-[140px] md:flex-1 bg-green-50 border-2 border-green-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{sessions.stats.completed_this_month}</div>
              <div className="text-xs text-green-700 font-bold">THIS MONTH</div>
            </div>
            
            {/* Allowance (External KJ only) */}
            {sessions.stats.is_external_kj && (
              <div className="flex-shrink-0 w-[140px] md:flex-1 bg-amber-50 border-2 border-amber-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-amber-600">${sessions.stats.allowance_earned_this_month}</div>
                <div className="text-xs text-amber-700 font-bold">EARNED</div>
              </div>
            )}
            
            {/* KJ Type Badge */}
            <div className="flex-shrink-0 w-[140px] md:flex-1 bg-gray-50 border-2 border-gray-200 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-600">
                {sessions.stats.is_external_kj ? '🎤' : '🏠'}
              </div>
              <div className="text-xs text-gray-700 font-bold">
                {sessions.stats.is_external_kj ? 'EXTERNAL' : 'MEMBER'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Mobile */}
      <div className="md:hidden sticky top-[68px] z-30 bg-white border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'upcoming' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-100 text-gray-600'
            }`}
            data-testid="tab-upcoming"
          >
            <Calendar size={16} />
            Upcoming ({sessions.upcoming_sessions.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'past' 
                ? 'bg-gray-700 text-white' 
                : 'bg-gray-100 text-gray-600'
            }`}
            data-testid="tab-past"
          >
            <CheckCircle size={16} />
            Past ({sessions.past_sessions.length})
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Mobile View - Tab Content */}
        <div className="md:hidden">
          {activeTab === 'upcoming' && (
            <div className="space-y-4">
              {sessions.upcoming_sessions.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                  <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
                  <p className="font-bold text-gray-500">No upcoming sessions</p>
                  <p className="text-sm text-gray-400">Your assigned bookings will appear here</p>
                </div>
              ) : (
                sessions.upcoming_sessions.map((session) => (
                  <SessionCard key={session.booking_code} session={session} isUpcoming={true} onRequestSwap={handleRequestSwap} />
                ))
              )}
            </div>
          )}
          
          {activeTab === 'past' && (
            <div className="space-y-4">
              {sessions.past_sessions.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                  <CheckCircle size={48} className="mx-auto mb-3 text-gray-300" />
                  <p className="font-bold text-gray-500">No past sessions</p>
                  <p className="text-sm text-gray-400">Your completed work will appear here</p>
                </div>
              ) : (
                sessions.past_sessions.slice(0, expandedPast ? undefined : 5).map((session) => (
                  <SessionCard key={session.booking_code} session={session} isUpcoming={false} onRequestSwap={handleRequestSwap} />
                ))
              )}
              
              {sessions.past_sessions.length > 5 && (
                <button
                  onClick={() => setExpandedPast(!expandedPast)}
                  className="w-full py-3 bg-gray-100 text-gray-600 rounded-lg font-bold flex items-center justify-center gap-2"
                >
                  {expandedPast ? (
                    <>
                      <ChevronUp size={18} />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown size={18} />
                      Show More ({sessions.past_sessions.length - 5} more)
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Desktop View - Side by Side */}
        <div className="hidden md:grid md:grid-cols-2 gap-6">
          {/* Upcoming Sessions */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-purple-700">
              <Calendar size={24} />
              Upcoming Sessions
            </h2>
            <div className="space-y-4">
              {sessions.upcoming_sessions.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                  <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
                  <p className="font-bold text-gray-500">No upcoming sessions</p>
                  <p className="text-sm text-gray-400">Your assigned bookings will appear here</p>
                </div>
              ) : (
                sessions.upcoming_sessions.map((session) => (
                  <SessionCard key={session.booking_code} session={session} isUpcoming={true} onRequestSwap={handleRequestSwap} />
                ))
              )}
            </div>
          </div>
          
          {/* Past Sessions */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-600">
              <CheckCircle size={24} />
              Past Sessions
            </h2>
            <div className="space-y-4">
              {sessions.past_sessions.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                  <CheckCircle size={48} className="mx-auto mb-3 text-gray-300" />
                  <p className="font-bold text-gray-500">No past sessions</p>
                </div>
              ) : (
                <>
                  {sessions.past_sessions.slice(0, expandedPast ? undefined : 3).map((session) => (
                    <SessionCard key={session.booking_code} session={session} isUpcoming={false} onRequestSwap={handleRequestSwap} />
                  ))}
                  
                  {sessions.past_sessions.length > 3 && (
                    <button
                      onClick={() => setExpandedPast(!expandedPast)}
                      className="w-full py-3 bg-gray-100 text-gray-600 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-gray-200"
                    >
                      {expandedPast ? (
                        <>
                          <ChevronUp size={18} />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown size={18} />
                          Show {sessions.past_sessions.length - 3} More
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* KJ Guidelines Download */}
        <div className="mt-8 bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="font-bold text-purple-800 flex items-center gap-2">
                <FileDown size={18} />
                KJ Guidelines &amp; SOP
              </h3>
              <p className="text-sm text-purple-600 mt-1">Download the official KJ guidelines, SOP, and emergency contacts</p>
            </div>
            <a
              href={`${API}/kj-guidelines-pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 active:bg-purple-800 transition-colors text-sm shrink-0"
              data-testid="download-kj-guidelines-btn"
            >
              <FileDown size={16} />
              Download PDF
            </a>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
            <AlertCircle size={18} />
            Session Info
          </h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li><strong>PENDING PAYMENT:</strong> Awaiting payment from resident</li>
            <li><strong>CONFIRMED:</strong> Payment verified, session is confirmed</li>
            <li><strong>REQUEST SWAP:</strong> Can&apos;t attend? Request a swap to find a replacement</li>
            {sessions.stats.is_external_kj && (
              <li><strong>ALLOWANCE:</strong> $35 credited per completed session</li>
            )}
          </ul>
        </div>
      </main>
      
      {/* Swap Request Dialog */}
      <SwapRequestDialog
        isOpen={showSwapDialog}
        onClose={() => {
          setShowSwapDialog(false);
          setSelectedBookingForSwap(null);
        }}
        booking={selectedBookingForSwap}
        onSwapRequested={handleSwapRequested}
      />
    </div>
  );
};

export default KJConsolePage;
